import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, LogOut, BookOpen, Rocket, Download, FileText, Lightbulb } from 'lucide-react';
import MetricCard from '@/components/MetricCard';
import WeeklyDataEntry from '@/components/WeeklyDataEntry';
import MetricTimeline from '@/components/MetricTimeline';
import GoalsSection from '@/components/GoalsSection';
import HealthScoreSection from '@/components/HealthScoreSection';
import MarketSentimentSection from '@/components/MarketSentimentSection';
import { useNavigate } from 'react-router-dom';

const Dashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [startups, setStartups] = useState([]);
  const [selectedStartup, setSelectedStartup] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewStartup, setShowNewStartup] = useState(false);
  const [newStartup, setNewStartup] = useState({ name: '', description: '', industry: '', currency: 'USD' });
  const [timelineKey, setTimelineKey] = useState(0); // For refreshing timeline

  const currencies = [
    { code: 'USD', name: 'US Dollar', symbol: '$' },
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'GBP', name: 'British Pound', symbol: '£' },
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
    { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [startupsRes, subRes] = await Promise.all([
        axios.get(`${API}/startups`, { withCredentials: true }),
        axios.get(`${API}/subscription`, { withCredentials: true })
      ]);
      setStartups(startupsRes.data);
      setSubscription(subRes.data);
      if (startupsRes.data.length > 0) {
        setSelectedStartup(startupsRes.data[0]);
        loadMetrics(startupsRes.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadMetrics = async (startupId) => {
    try {
      const response = await axios.get(`${API}/metrics/${startupId}`, { withCredentials: true });
      setMetrics(response.data);
    } catch (error) {
      toast.error('Failed to load metrics');
    }
  };

  const handleCreateStartup = async () => {
    if (!newStartup.name.trim()) {
      toast.error('Startup name is required');
      return;
    }
    try {
      const response = await axios.post(`${API}/startups`, newStartup, { withCredentials: true });
      setStartups([...startups, response.data]);
      setSelectedStartup(response.data);
      loadMetrics(response.data.id);
      setShowNewStartup(false);
      setNewStartup({ name: '', description: '', industry: '', currency: 'USD' });
      toast.success('Startup created successfully!');
    } catch (error) {
      toast.error('Failed to create startup');
    }
  };

  const handleMetricUpdate = async (metricId, inputs) => {
    try {
      const response = await axios.patch(`${API}/metrics/${metricId}`, { inputs }, { withCredentials: true });
      setMetrics(metrics.map(m => m.id === metricId ? response.data : m));
      toast.success('Metric updated!');
    } catch (error) {
      toast.error('Failed to update metric');
    }
  };

  const handleDownloadReport = async () => {
    if (!selectedStartup) {
      toast.error('Please select a startup first');
      return;
    }
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API}/startups/${selectedStartup.id}/report`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedStartup.name}_Metrics_Report.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Report downloaded!');
    } catch (error) {
      toast.error('Failed to download report');
    }
  };

  const handleWeeklyEntrySubmitted = () => {
    // Refresh metrics and timeline
    if (selectedStartup) {
      loadMetrics(selectedStartup.id);
      setTimelineKey(prev => prev + 1); // Force timeline refresh
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" data-testid="dashboard">
      {/* Header */}
      <nav className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <div className="w-9 h-9 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
                <Rocket className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                StartupBlackbox
              </span>
            </div>
            <Button
              variant="ghost"
              onClick={() => navigate('/knowledge')}
              data-testid="nav-knowledge-btn"
              className="text-slate-300 hover:text-cyan-400"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Knowledge Base
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/validate')}
              data-testid="nav-validate-btn"
              className="text-slate-300 hover:text-cyan-400"
            >
              <Lightbulb className="w-4 h-4 mr-2" />
              Validate Idea
            </Button>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <img src={user.picture || 'https://via.placeholder.com/40'} alt={user.name} className="w-9 h-9 rounded-full border-2 border-cyan-500" />
              <span className="text-sm text-slate-300">{user.name}</span>
            </div>
            <Button
              variant="ghost"
              onClick={onLogout}
              data-testid="logout-btn"
              className="text-slate-400 hover:text-red-400"
              type="button"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-8">
        {/* Startup Selection */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            {startups.map(startup => (
              <Button
                key={startup.id}
                onClick={() => {
                  setSelectedStartup(startup);
                  loadMetrics(startup.id);
                }}
                data-testid={`startup-select-${startup.id}`}
                variant={selectedStartup?.id === startup.id ? 'default' : 'outline'}
                className={selectedStartup?.id === startup.id
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white'
                  : 'border-slate-700 text-slate-300 hover:border-cyan-500'}
              >
                {startup.name}
              </Button>
            ))}
            <Dialog open={showNewStartup} onOpenChange={setShowNewStartup}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="new-startup-btn" className="border-slate-700 text-slate-300 hover:border-cyan-500">
                  <Plus className="w-4 h-4 mr-2" />
                  Quick Add
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-900 border-slate-700" aria-describedby="new-startup-description">
                <DialogHeader>
                  <DialogTitle className="text-slate-100">Create New Startup</DialogTitle>
                </DialogHeader>
                <p id="new-startup-description" className="sr-only">Enter details to create a new startup and track its metrics</p>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="name" className="text-slate-300">Startup Name *</Label>
                    <Input
                      id="name"
                      data-testid="startup-name-input"
                      value={newStartup.name}
                      onChange={(e) => setNewStartup({ ...newStartup, name: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-slate-100"
                    />
                  </div>
                  <div>
                    <Label htmlFor="industry" className="text-slate-300">Industry</Label>
                    <Input
                      id="industry"
                      data-testid="startup-industry-input"
                      value={newStartup.industry}
                      onChange={(e) => setNewStartup({ ...newStartup, industry: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-slate-100"
                    />
                  </div>
                  <div>
                    <Label htmlFor="currency" className="text-slate-300">Currency *</Label>
                    <Select
                      value={newStartup.currency}
                      onValueChange={(value) => setNewStartup({ ...newStartup, currency: value })}
                    >
                      <SelectTrigger
                        id="currency"
                        data-testid="startup-currency-select"
                        className="bg-slate-800 border-slate-700 text-slate-100"
                      >
                        <SelectValue placeholder="Select currency" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        {currencies.map(curr => (
                          <SelectItem
                            key={curr.code}
                            value={curr.code}
                            data-testid={`currency-${curr.code}`}
                            className="text-slate-100 focus:bg-slate-700 focus:text-slate-100"
                          >
                            {curr.code} - {curr.name} ({curr.symbol})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="description" className="text-slate-300">Description</Label>
                    <Textarea
                      id="description"
                      data-testid="startup-description-input"
                      value={newStartup.description}
                      onChange={(e) => setNewStartup({ ...newStartup, description: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-slate-100"
                      rows={3}
                    />
                  </div>
                  <Button
                    onClick={handleCreateStartup}
                    data-testid="create-startup-btn"
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white"
                  >
                    Create Startup
                  </Button>
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-slate-700" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-slate-900 px-2 text-slate-500">or</span>
                    </div>
                  </div>
                  <Button
                    onClick={() => { setShowNewStartup(false); navigate('/onboarding'); }}
                    variant="outline"
                    data-testid="full-onboarding-btn"
                    className="w-full border-slate-700 text-slate-300 hover:border-cyan-500"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Full Onboarding (Recommended for Funding)
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          
          {selectedStartup && (
            <Button
              onClick={handleDownloadReport}
              data-testid="download-report-btn"
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Report
            </Button>
          )}
        </div>

        {/* No Startup State */}
        {startups.length === 0 && (
          <div className="text-center py-20" data-testid="no-startup-state">
            <Rocket className="w-20 h-20 mx-auto mb-6 text-slate-600" />
            <h2 className="text-2xl font-semibold mb-3 text-slate-300">No Startups Yet</h2>
            <p className="text-slate-400 mb-6">Create your first startup to start tracking metrics</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                onClick={() => navigate('/onboarding')}
                data-testid="start-onboarding-btn"
                className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white"
              >
                <FileText className="w-4 h-4 mr-2" />
                Full Onboarding (For Funding)
              </Button>
              <Button
                onClick={() => setShowNewStartup(true)}
                variant="outline"
                data-testid="create-first-startup-btn"
                className="border-slate-700 text-slate-300 hover:border-cyan-500"
              >
                <Plus className="w-4 h-4 mr-2" />
                Quick Add Startup
              </Button>
            </div>
          </div>
        )}

        {/* Metrics Grid */}
        {selectedStartup && (
          <div data-testid="metrics-grid">
            <h2 className="text-2xl font-bold mb-6 text-slate-100">
              {selectedStartup.name} Metrics
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
              {metrics.map(metric => (
                <MetricCard
                  key={metric.id}
                  metric={metric}
                  onUpdate={handleMetricUpdate}
                  currency={selectedStartup.currency || 'USD'}
                />
              ))}
            </div>

            {/* Health Score & Charts */}
            <div className="mb-8">
              <HealthScoreSection startupId={selectedStartup.id} />
            </div>

            {/* Weekly Data Entry */}
            <div className="grid lg:grid-cols-3 gap-8 mb-8">
              <div className="lg:col-span-1">
                <WeeklyDataEntry 
                  startupId={selectedStartup.id}
                  onEntrySubmitted={handleWeeklyEntrySubmitted}
                />
              </div>
              <div className="lg:col-span-2">
                <MarketSentimentSection startupId={selectedStartup.id} />
              </div>
            </div>

            {/* Timeline */}
            <MetricTimeline 
              key={timelineKey}
              startupId={selectedStartup.id}
              currency={selectedStartup.currency || 'USD'}
            />

            {/* Goals Section */}
            <div className="mt-8">
              <GoalsSection 
                startupId={selectedStartup.id}
                currency={selectedStartup.currency || 'USD'}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;